import React, { useState } from 'react';
import clsx from 'clsx';

import { getTdClassName } from './getTdClassName';

export type ColumnType<T> = {
  header: React.ReactNode;
  headerStyle?: React.CSSProperties;

  sortName?: string;
  getValue: (row: T, index: number) => React.ReactNode;

  truncate?: boolean;
  cellStyle?: React.CSSProperties;
  cellClassName?: string;
  cellContentClassName?: string;
  align?: 'left' | 'right' | 'center';
};

export type ConfigType<T> = {
  columns: ColumnType<T>[];
};

type TableProps<T = any> = {
  data: T[];
  loading?: boolean;
  className?: string;
  clampCells?: boolean;
  withHeader?: boolean;
  config: ConfigType<T>;
  emptyMessage?: string;
  cellsContentClassName?: string;
  verticalAlign?: 'top' | 'middle';
  onRowClick?: (row: T) => void;
  cellsClassName?: string;
  headersClassName?: string;

  sort?: SortState;
  onSort?: (newSort: string) => void;
};

const TableHeader = <T,>({
  sort,
  onSort,
  config,

  headersClassName,
}: Pick<
  TableProps<T>,
  'config' | 'sort' | 'onSort' | 'headersClassName'
> & {}) => {
  return (
    <thead>
      <tr className={clsx(headersClassName)}>
        {config.columns.map((column, index) => {
          const isFirst = index === 0;
          const isLast = index >= config.columns.length - 1;

          const { name, direction: dir } = sort ?? {};
          const sortName = column.sortName;
          const showSort = !!sortName && name === sortName && !!dir;
          const iconElement = dir === 'asc' ? '↑' : '↓';
          const commonClassName = clsx(
            isFirst && 'rounded-tl-sm',
            isLast && 'rounded-tr-sm'
          );

          return (
            <th
              key={index}
              style={column.headerStyle}
              className={clsx(
                'bg-light text-textLight font-medium truncate p-0 m-0 overflow-hidden border-0',

                commonClassName,

                column.align === 'right'
                  ? 'text-right'
                  : column.align === 'center'
                    ? 'text-center'
                    : 'text-left'
              )}
            >
              <div
                className={clsx(
                  commonClassName,

                  isLast && 'border-r',
                  isFirst && 'border-l',

                  sortName ? 'cursor-pointer' : '',
                  'flex gap-x-xs w-full py-md px-xl',
                  column.align === 'right' && 'justify-end',
                  column.align === 'center' && 'justify-center',
                  'border-t border-active',

                  (!column.align || column.align === 'left') && 'justify-start'
                )}
                onClick={sortName ? () => onSort?.(sortName) : undefined}
              >
                {showSort && iconElement}
                {column.header}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const TableRows = <T,>({
  data,
  config,
  clampCells,
  cellsClassName,
  cellsContentClassName,

  verticalAlign = 'middle',
  onRowClick,
}: Pick<
  TableProps<T>,
  | 'data'
  | 'clampCells'
  | 'config'
  | 'cellsClassName'
  | 'cellsContentClassName'
  | 'verticalAlign'
  | 'onRowClick'
>) => {
  return (
    <>
      {data?.map((row, rowIndex) => {
        return (
          <React.Fragment key={rowIndex}>
            <tr
              onClick={e => {
                e.stopPropagation();
                onRowClick?.(row);
              }}
              className={clsx(
                'bg-contrast group/tablerow',
                onRowClick ? 'cursor-pointer' : '',
                rowIndex === data.length - 1 ? 'border-b-active border-b' : '',
                cellsClassName
              )}
            >
              {config.columns.map((column, index) => {
                const isFirstColumn = index === 0;
                const isLastColumn = index === config.columns.length - 1;

                const tdClassName = clsx(
                  getTdClassName(column, verticalAlign),
                  'p-0',
                  isLastColumn ? 'border-r border-active' : '',
                  isFirstColumn ? 'border-l border-active' : '',
                  {
                    'text-right': column.align === 'right',
                    'text-center': column.align === 'center',
                    'text-left': !column.align || column.align === 'left',
                  }
                );

                return (
                  <td
                    key={index}
                    style={column.cellStyle}
                    className={tdClassName}
                  >
                    <div
                      className={clsx(
                        'py-lg px-xl',

                        column.truncate
                          ? 'truncate'
                          : clampCells && 'line-clamp-4 break-words',
                        cellsContentClassName,
                        column.cellContentClassName,
                        column.align === 'center' && 'text-center',
                        column.align === 'right' && 'text-right',
                        (!column.align || column.align === 'left') &&
                          'text-left'
                      )}
                    >
                      {column.getValue(row, rowIndex)}
                    </div>
                  </td>
                );
              })}
            </tr>
          </React.Fragment>
        );
      })}
    </>
  );
};

type TableLoaderProps = {
  rowsCount?: number;
  columnsCount: number;
};

const TableLoader: React.FC<TableLoaderProps> = ({
  rowsCount = 4,
  columnsCount,
}) => {
  const element = (
    <tr>
      <td colSpan={columnsCount} className="px-4">
        <div className="py-8 px-4 bg-gray-200 animate-pulse rounded-card">
          <div className="h-10 bg-gray-300 rounded-lg" />
        </div>
      </td>
    </tr>
  );

  return (
    <>
      {Array.from({ length: rowsCount }).map((_item, index) => (
        <React.Fragment key={index}>{element}</React.Fragment>
      ))}
    </>
  );
};

const Table = <T,>({
  data = [],
  config,
  className,
  emptyMessage,
  loading = false,
  withHeader = true,
  clampCells = true,
  cellsClassName,
  headersClassName,
  cellsContentClassName,
  verticalAlign = 'middle',
  onRowClick,
  sort,
  onSort,
}: TableProps<T>): React.ReactNode => {
  if (!data?.length && !!emptyMessage && !loading) {
    return emptyMessage;
  }

  return (
    <table
      className={clsx(
        className,
        'w-full border-collapse border-spacing-0 h-fit'
      )}
    >
      {!!withHeader && (
        <TableHeader
          sort={sort}
          onSort={onSort}
          config={config}
          headersClassName={headersClassName}
        />
      )}
      <tbody>
        {loading ? (
          <TableLoader columnsCount={config.columns.length} />
        ) : (
          <TableRows
            data={data}
            config={config}
            clampCells={clampCells}
            verticalAlign={verticalAlign}
            cellsClassName={cellsClassName}
            cellsContentClassName={cellsContentClassName}
            onRowClick={onRowClick}
          />
        )}
      </tbody>
    </table>
  );
};

export default React.memo(Table) as typeof Table;
